'use client';

import { useEffect, useState } from 'react';

import { Card, cn } from '@endaoment-frontend/design-system';

import type { Heading } from '../../lib/ghost/headings';

interface TableOfContentsProps {
  headings: ReadonlyArray<Heading>;
  className?: string;
}

/* How far down the window a heading has to reach before its section counts as
   the one being read: under the header, with a little room. */
const READING_LINE = 0.3;

/**
 * The list of an article's sections, with the one being read lit. The
 * heading in the body is lit with it, through a data attribute
 * PostBody styles: the body is server HTML, so this is the one place that can
 * touch it. Renders nothing under two headings: a list of one is a heading
 * the reader can already see.
 */
export const TableOfContents = ({ headings, className }: TableOfContentsProps) => {
  const [current, setCurrent] = useState<string | null>(null);

  useEffect(() => {
    if (headings.length < 2) return;
    const targets = headings.map(({ id }) => document.getElementById(id)).filter(el => el !== null);

    /* The last heading above the reading line. A scroll listener rather than
       an observer: between two headings nothing intersects anything, and the
       section being read is still the one whose heading went past. */
    const update = () => {
      const line = window.innerHeight * READING_LINE;
      let active: string | null = null;
      for (const el of targets) if (el.getBoundingClientRect().top <= line) active = el.id;
      /* At the bottom of the page the reader is in the last section whether
         or not its heading ever crossed the line: a short last section never
         gets that far. Within a pixel, for a fractional scroll position. Only
         on a page that scrolls, or one that fits the window would open lit. */
      const last = targets[targets.length - 1];
      const { scrollHeight } = document.documentElement;
      const atBottom = scrollHeight > window.innerHeight && window.innerHeight + window.scrollY >= scrollHeight - 1;
      setCurrent(atBottom && last ? last.id : active);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [headings]);

  useEffect(() => {
    for (const { id } of headings) document.getElementById(id)?.toggleAttribute('data-current', id === current);
  }, [current, headings]);

  if (headings.length < 2) return null;

  return (
    <Card padding='sm' elevation='none' className={className}>
      {/* Namespaced, so a post heading titled "On this page" cannot take the same id. */}
      <nav aria-labelledby='article-toc'>
        <h2 id='article-toc' className='label m-0 text-scarlet-700'>
          On this page
        </h2>
        <ol className='paragraph m-0 mt-2 flex list-none flex-col divide-y divide-slate-200 p-0'>
          {/* An h3 is indented behind a short dash. */}
          {headings.map(({ id, text, level }) => (
            <li
              key={id}
              className={cn(
                'py-2 first:pt-1 last:pb-0',
                level === 3 &&
                  /* The dash sits on the first line's middle: the row's top
                     padding plus half a line at the step's 150% leading. */
                  'relative pl-4 before:absolute before:top-[calc(0.5rem+0.75em)] before:left-0 before:h-px before:w-2 before:bg-slate-300',
              )}>
              <a
                href={`#${id}`}
                aria-current={id === current ? 'location' : undefined}
                className={cn(
                  'text-slate-700 transition-colors duration-200 ease-out hover:text-scarlet-700',
                  id === current && 'font-medium text-scarlet-700',
                )}>
                {text}
              </a>
            </li>
          ))}
        </ol>
      </nav>
    </Card>
  );
};
